import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { gameUIVariants } from '../../lib/animations';

interface CosmicInputProps {
  type?: 'text' | 'password' | 'email' | 'number' | 'search';
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  success?: string;
  label?: string;
  icon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'search' | 'terminal';
  className?: string;
  required?: boolean;
  autoComplete?: string;
}

const CosmicInput: React.FC<CosmicInputProps> = ({
  type = 'text',
  placeholder,
  value = '',
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  error,
  success,
  label,
  icon,
  size = 'md',
  variant = 'default',
  className = '',
  required = false,
  autoComplete
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-2 text-sm';
      case 'md':
        return 'px-4 py-3 text-base';
      case 'lg':
        return 'px-6 py-4 text-lg';
      default:
        return 'px-4 py-3 text-base';
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'search':
        return 'pl-10 pr-4';
      case 'terminal':
        return 'font-mono bg-cosmic-black/50 border-stellar-cyan/30';
      default:
        return '';
    }
  };

  const getStatusColor = () => {
    if (error) return 'border-danger-red text-danger-red';
    if (success) return 'border-success-emerald text-success-emerald';
    if (isFocused) return 'border-stellar-blue text-stellar-blue';
    return 'border-stellar-cyan/30 text-stellar-cyan';
  };

  const getStatusGlow = () => {
    if (error) return '0 0 10px var(--danger-red)';
    if (success) return '0 0 10px var(--success-emerald)';
    if (isFocused) return '0 0 10px var(--stellar-blue)';
    return 'none';
  };

  const handleFocus = () => {
    setIsFocused(true);
    setIsTyping(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    setIsTyping(false);
    onBlur?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 1000);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-mono text-stellar-cyan mb-2 uppercase tracking-wider">
          {label}
          {required && <span className="text-danger-red ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <motion.div
        className={`
          relative bg-cosmic-dark/80 backdrop-blur-md border-2 rounded-lg
          transition-all duration-300 group
          ${getSizeStyles()}
          ${getVariantStyles()}
          ${getStatusColor()}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}
        `}
        variants={gameUIVariants}
        whileHover={disabled ? undefined : { scale: 1.02 }}
        style={{
          boxShadow: getStatusGlow()
        }}
      >
        {/* Icon */}
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stellar-blue">
            {icon}
          </div>
        )}

        {/* Input Field */}
        <input
          type={type}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          className={`
            w-full bg-transparent border-none outline-none
            text-white placeholder-stellar-cyan/50
            font-mono
            ${icon ? 'pl-8' : ''}
          `}
        />

        {/* Scan Line Effect */}
        {isFocused && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute top-0 left-0 w-full h-0.5 bg-stellar-blue"
              animate={{
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        )}

        {/* Typing Indicator */}
        {isTyping && variant === 'terminal' && (
          <motion.div
            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-2 h-4 bg-stellar-blue"
            animate={{
              opacity: [1, 0, 1]
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        )}

        {/* Status Icons */}
        {error && (
          <motion.div
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger-red"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            ⚠
          </motion.div>
        )}

        {success && (
          <motion.div
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-success-emerald"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            ✓
          </motion.div>
        )}

        {/* Focus Glow */}
        {isFocused && (
          <motion.div
            className="absolute inset-0 rounded-lg border-stellar-blue/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
            }}
          />
        )}
      </motion.div>

      {/* Status Messages */}
      {(error || success) && (
        <motion.div
          className={`mt-2 text-xs font-mono ${
            error ? 'text-danger-red' : 'text-success-emerald'
          }`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {error || success}
        </motion.div>
      )}
    </div>
  );
};

// Specialized input variants
export const SearchInput: React.FC<Omit<CosmicInputProps, 'variant'>> = (props) => (
  <CosmicInput variant="search" type="search" {...props} />
);

export const TerminalInput: React.FC<Omit<CosmicInputProps, 'variant'>> = (props) => (
  <CosmicInput variant="terminal" {...props} />
);

// Textarea Component
interface CosmicTextareaProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  success?: string;
  label?: string;
  rows?: number;
  className?: string;
  required?: boolean;
}

export const CosmicTextarea: React.FC<CosmicTextareaProps> = ({
  placeholder,
  value = '',
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  error,
  success,
  label,
  rows = 4,
  className = '',
  required = false
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const getStatusColor = () => {
    if (error) return 'border-danger-red text-danger-red';
    if (success) return 'border-success-emerald text-success-emerald';
    if (isFocused) return 'border-stellar-blue text-stellar-blue';
    return 'border-stellar-cyan/30 text-stellar-cyan';
  };

  const getStatusGlow = () => {
    if (error) return '0 0 10px var(--danger-red)';
    if (success) return '0 0 10px var(--success-emerald)';
    if (isFocused) return '0 0 10px var(--stellar-blue)';
    return 'none';
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-mono text-stellar-cyan mb-2 uppercase tracking-wider">
          {label}
          {required && <span className="text-danger-red ml-1">*</span>}
        </label>
      )}

      <motion.div
        className={`
          relative bg-cosmic-dark/80 backdrop-blur-md border-2 rounded-lg
          transition-all duration-300
          ${getStatusColor()}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}
        `}
        variants={gameUIVariants}
        whileHover={disabled ? undefined : { scale: 1.02 }}
        style={{
          boxShadow: getStatusGlow()
        }}
      >
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          rows={rows}
          className="w-full bg-transparent border-none outline-none text-white placeholder-stellar-cyan/50 font-mono p-4 resize-none"
        />

        {isFocused && (
          <motion.div
            className="absolute inset-0 rounded-lg border-stellar-blue/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
            }}
          />
        )}
      </motion.div>

      {(error || success) && (
        <motion.div
          className={`mt-2 text-xs font-mono ${
            error ? 'text-danger-red' : 'text-success-emerald'
          }`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {error || success}
        </motion.div>
      )}
    </div>
  );
};

export default CosmicInput; 